/**
 * Case-insensitive, whole-word, plain-text keyword matching.
 *
 * "Whole-word" means a keyword only matches when it is not adjacent to another
 * alphanumeric character, so `review` matches `Sprint review` but not
 * `reviewed`. Keywords may contain internal punctuation (`1:1`) or spaces
 * (`meeting notes`, `let's sync`); internal whitespace in a keyword matches any
 * run of whitespace in the text so that line breaks between words still match.
 *
 * No page content leaves this module — matching returns a boolean only.
 */

// Escape characters that are significant in a RegExp so the keyword is matched
// literally.
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A word character for boundary purposes: any Unicode letter or number. Using
// look-around (rather than \b) lets keywords whose edges are non-word
// characters — e.g. `1:1` — still be treated as whole tokens.
const BOUNDARY_START = '(?<![\\p{L}\\p{N}])';
const BOUNDARY_END = '(?![\\p{L}\\p{N}])';

const buildKeywordRegExp = (keyword: string): RegExp | null => {
	const trimmed = keyword.trim();
	if (!trimmed) {
		return null;
	}
	// Escape, then let any internal whitespace match a run of whitespace.
	const pattern = escapeRegExp(trimmed).replace(/\\?\s+/g, '\\s+');
	return new RegExp(`${BOUNDARY_START}${pattern}${BOUNDARY_END}`, 'iu');
};

/**
 * Returns true when `text` contains `keyword` as a whole word,
 * case-insensitively.
 */
export const matchesKeyword = (text: string, keyword: string): boolean => {
	if (!text) {
		return false;
	}
	const regExp = buildKeywordRegExp(keyword);
	return regExp ? regExp.test(text) : false;
};

/**
 * Returns true when `text` contains at least one keyword from `keywords` as a
 * whole word, case-insensitively. Empty text or an empty list never matches.
 */
export const matchesAnyKeyword = (text: string, keywords: readonly string[]): boolean => {
	if (!text || keywords.length === 0) {
		return false;
	}
	return keywords.some((keyword) => matchesKeyword(text, keyword));
};
